import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Radio,
  Scan,
  Calendar,
  FileText,
  LogOut,
  User,
  MapPin,
  Cpu,
  CloudOff,
  CreditCard,
  Shield,
  Users,
  Building2,
  HeadphonesIcon,
  Calculator,
  LayoutTemplate,
  ChevronDown,
  ChevronRight,
  Waves,
  Settings2,
  UserCircle,
  Sparkles,
  Usb,
  Sliders,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { NotificationCenter } from "@/components/NotificationCenter";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useNotifications } from "@/hooks/useNotifications";
import CountdownBanner from "@/components/updates/CountdownBanner";
import NewBadge from "@/components/updates/NewBadge";
import { isFeatureAvailable } from "@/lib/featureFlags";
import { useUSBConnection } from "@/contexts/USBConnectionContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Grouped navigation structure - Simplified
const navGroups = [
  {
    id: "dashboard",
    labelKey: "nav.dashboard",
    icon: Activity,
    type: "single",
    to: "/dashboard",
  },
  {
    id: "rf",
    label: "RF Control",
    icon: Radio,
    type: "group",
    items: [
      { to: "/systems", icon: Waves, labelKey: "nav.systems" },
      { to: "/hardware", icon: Cpu, labelKey: "nav.hardware", isNew: true },
      { to: "/scan", icon: Scan, labelKey: "nav.scan" },
    ],
  },
  {
    id: "events",
    label: "Eventos",
    icon: Calendar,
    type: "group",
    items: [
      { to: "/events", icon: Calendar, labelKey: "nav.events" },
      { to: "/venues", icon: Building2, labelKey: "nav.venues" },
      { to: "/templates", icon: LayoutTemplate, labelKey: "nav.templates" },
    ],
  },
  {
    id: "tools",
    label: "Herramientas",
    icon: Settings2,
    type: "group",
    items: [
      { to: "/frequencies", icon: Sliders, labelKey: "nav.frequencies" },
      { to: "/intermodulation", icon: Calculator, labelKey: "nav.intermodulation" },
      { to: "/chile-frequencies", icon: MapPin, labelKey: "nav.chileDb" },
      { to: "/reports", icon: FileText, labelKey: "nav.reports" },
      { to: "/offline", icon: CloudOff, labelKey: "common.offline" },
    ],
  },
  {
    id: "account",
    label: "Cuenta",
    icon: UserCircle,
    type: "group",
    items: [
      { to: "/subscription", icon: CreditCard, labelKey: "nav.subscription" },
      { to: "/team", icon: Users, labelKey: "nav.team" },
      { to: "/changelog", icon: Sparkles, label: "Novedades", isNew: true },
      { to: "/support", icon: HeadphonesIcon, labelKey: "nav.support" },
    ],
  },
];

const adminNavItems = [
  { to: "/admin", icon: Shield, labelKey: "nav.admin" },
];

// Get the group ID that contains a given path
const getGroupForPath = (path) => {
  for (const group of navGroups) {
    if (group.type === "single" && group.to === path) {
      return group.id;
    }
    if (group.type === "group" && group.items) {
      for (const item of group.items) {
        if (path.startsWith(item.to)) {
          return group.id;
        }
      }
    }
  }
  return null;
};

export const Layout = ({ children, user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const { isConnected, notifications, clearNotifications } = useNotifications(token);
  
  // USB Connection state
  const { serialConnected, serialDeviceInfo, activeDevice } = useUSBConnection();

  // Track expanded groups - load from localStorage
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem("avx_nav_expanded");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return {};
  });

  // Auto-expand the group containing current path
  useEffect(() => {
    const currentGroup = getGroupForPath(location.pathname);
    if (currentGroup && !expandedGroups[currentGroup]) {
      setExpandedGroups(prev => ({ ...prev, [currentGroup]: true }));
    }
  }, [location.pathname]);

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem("avx_nav_expanded", JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  const isGroupActive = (group) => {
    if (group.type === "single") {
      return location.pathname === group.to;
    }
    return group.items?.some(item => location.pathname.startsWith(item.to));
  };

  return (
    <div className="flex min-h-screen bg-[#050505]">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="sidebar-logo">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-[#34C759] flex items-center justify-center">
              <Radio className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-sm text-[#F4F4F5]">
                AVX RF PRO
              </h1>
              <p className="text-[10px] text-[#71717A] tracking-wider">
                ANLEON CHILE
              </p>
            </div>
          </div>
          {/* Notification Bell in Header */}
          <div className="ml-auto flex items-center gap-1">
            {/* USB Connection Indicator */}
            {serialConnected && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="flex items-center gap-1 px-2 py-1 bg-[#34C759]/20 rounded-md cursor-pointer hover:bg-[#34C759]/30 transition-colors"
                      onClick={() => navigate('/hardware')}
                    >
                      <Usb className="w-3.5 h-3.5 text-[#34C759]" />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#34C759] animate-pulse" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-[#1A1A1A] border-white/10">
                    <p className="text-xs">
                      <span className="text-[#34C759] font-medium">tinySA Conectado</span>
                      <br />
                      <span className="text-gray-400">
                        VID: {serialDeviceInfo?.vendorId} | PID: {serialDeviceInfo?.productId}
                      </span>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <LanguageSelector variant="icon" />
            <NotificationCenter
              notifications={notifications}
              isConnected={isConnected}
              onClear={clearNotifications}
            />
          </div>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map((group) => (
            <div key={group.id} className="mb-1">
              {group.type === "single" ? (
                // Single item (Dashboard)
                <NavLink
                  to={group.to}
                  end
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${group.to.replace("/", "")}`}
                >
                  <group.icon className="w-4 h-4" />
                  <span>{t(group.labelKey)}</span>
                </NavLink>
              ) : (
                // Collapsible group
                <>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium uppercase tracking-wider rounded-sm transition-all ${
                      isGroupActive(group)
                        ? "text-[#34C759] bg-[#34C759]/5"
                        : "text-[#71717A] hover:text-[#A1A1AA] hover:bg-white/[0.02]"
                    }`}
                    data-testid={`nav-group-${group.id}`}
                  >
                    <group.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{group.label}</span>
                    {expandedGroups[group.id] ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                  </button>
                  
                  {/* Group items */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      expandedGroups[group.id] ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="ml-4 pl-2 border-l border-white/[0.06] mt-1 space-y-0.5">
                      {group.items.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `sidebar-link text-sm py-1.5 ${isActive ? "active" : ""}`
                          }
                          data-testid={`nav-${item.to.replace("/", "")}`}
                        >
                          <item.icon className="w-3.5 h-3.5" />
                          <span className="flex-1">{item.label || t(item.labelKey)}</span>
                          {item.isNew && <NewBadge featureId="changelog" />}
                          {item.to === "/hardware" && isFeatureAvailable('rf_network_control') && (
                            <NewBadge featureId="rf_network_control" />
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
          
          {/* Admin links - only show for admin role */}
          {user?.role === "admin" && (
            <>
              <div className="my-2 border-t border-white/[0.08]" />
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? "active" : ""} text-red-400`
                  }
                  data-testid={`nav-${item.to.replace("/", "")}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-white/[0.08]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-auto p-2 hover:bg-white/5"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center border border-white/10">
                  <User className="w-4 h-4 text-[#A1A1AA]" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-[#F4F4F5]">
                    {user?.name || "Usuario"}
                  </p>
                  <p className="text-xs text-[#71717A] capitalize">
                    {user?.role || "operator"}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-[#111111] border-white/10"
            >
              <DropdownMenuItem 
                className="text-[#A1A1AA] cursor-pointer"
                onClick={() => navigate("/profile")}
              >
                <User className="w-4 h-4 mr-2" />
                {t("nav.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-[#A1A1AA] cursor-pointer"
                onClick={() => navigate("/subscription")}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {t("nav.subscription")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-[#FF3B30] focus:text-[#FF3B30] cursor-pointer"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <CountdownBanner />
        {children}
      </main>
    </div>
  );
};

export default Layout;
